// Constants matching Vue 2 constants.js
export const BULK_DOWNLOAD_STATUS = {
  COMPLETED: 2,
  FAILED: 3,
  // DEV-7273: terminal AND partially successful. Vue 2 shipped this after both its detail
  // screens polled a 104 job forever and rendered "In Progress" indefinitely, because each
  // knew only COMPLETED and FAILED — see utils/bulk-download-status.ts.
  COMPLETED_WITH_ERRORS: 104,
} as const

export const BULK_DOWNLOAD_DESTINATION = {
  FILE: 1,
  SALESFORCE: 2,
  HUBSPOT: 3,
  PIPEDRIVE: 4,
  ZOHO: 5,
  SALESLOFT: 6,
  CLOSEIO: 7,
  DYNAMICS: 8,
  PARDOT: 9,
} as const

export const LIST_TYPE_CONTACT = 'Contacts'

export const EXCLUSION_REASON = {
  NO_CRITERIA_MATCH: 0,
  NO_PREFERRED_TITLE_MATCH: 1,
} as const

export const POLLING_INTERVAL_MS = 2000

export interface BulkDownloadInformation {
  BulkDestination: number
  Name: string
  Created: string
  TotalLeads: number
  TotalContacts: number
  TotalCreditsUsed: number
  // Split of TotalCreditsUsed. Both null on jobs that never recorded the company share —
  // the total is then shown alone rather than split dishonestly.
  CompanyCreditsUsed: number | null
  ContactCreditsUsed: number | null
  // null on legacy-engine jobs and pre-deploy documents; a written 0 is meaningful and renders.
  PreviouslyPurchasedContacts: number | null
  // Companies delivered without spending a credit. Same null rule as the contacts half; the
  // tile shows the sum of both (utils/bulk-download-stats.ts sumParts).
  PreviouslyPurchasedCompanies: number | null
  StepLabel: string
  IsStreaming: boolean
  RefreshBatchesTotal: number
  RefreshBatchesCompleted: number
  PushBatchesCompleted: number
  ReportFormat: string
  PercentCompleted: number
  Status: number
  StatusDetail: string
  ShouldRefreshContacts: boolean
  ListType: string
  PreferredTitle: string
  ProcessedLeads: number
  FailedLeads: number
  Errors: BulkDownloadError[]
  LeadExclusionDetails: LeadExclusionDetail[]
  IsRemovedFromList: boolean
  ListId: string
  SavedSearchIds: string[]
  RejectedEmailDetails: RejectedEmailDetail[]
  RestrictedInformation: RestrictedInfoDetail[]
  NoResultsFromSearch: boolean
  // A BackOffice-index export: it charges no credits, and its report can be too large to
  // download through the web app (see BoReportDownloadLocation).
  ShouldUseBoIndex: boolean
}

export interface BulkDownloadRefreshInfo {
  TotalLeads: number
  TotalContacts: number
  TotalCreditsUsed: number
  CompanyCreditsUsed: number | null
  ContactCreditsUsed: number | null
  PreviouslyPurchasedContacts: number | null
  PreviouslyPurchasedCompanies: number | null
  StepLabel: string
  IsStreaming: boolean
  RefreshBatchesTotal: number
  RefreshBatchesCompleted: number
  PushBatchesCompleted: number
  PercentCompleted: number
  Status: number
  StatusDetail: string
  ExceededMaxAttemptsCompleted: boolean
  ShouldRefreshContacts: boolean
  ListType: string
  ProcessedLeads: number
  FailedLeads: number
  Errors: BulkDownloadError[]
  LeadExclusionDetails: LeadExclusionDetail[]
  RejectedEmailDetails: RejectedEmailDetail[]
  IsRemovedFromList: boolean
  ListId: string
  NoResultsFromSearch: boolean
}

export interface BulkDownloadError {
  Key: string
  Name: string
  Error: string
}

export interface LeadExclusionDetail {
  SimpleUrl: string
  LeadExclusionReason: number
}

export interface RejectedEmailDetail {
  ContactName: string
  Title: string
  Email: string
  SimpleUrl: string
}

export interface RestrictedInfoDetail {
  ContactName: string
  Title: string
  Information: string
  Value: string
  // DEV-7604: the restriction source (TPS, GDPR, OrgGDPR, SuppressedByPlan, …) and the
  // server's display text for it. Older documents carry no Message.
  Restriction: string
  Message?: string | null
  SimpleUrl: string
  Date: string
}

export interface GroupedRestrictedContact {
  ContactName: string
  Title: string
  SimpleUrl: string
  EmailRestricted: boolean
  MobileRestricted: boolean
  EmailReason: string | null
  EmailMessage: string | null
  MobileReason: string | null
  MobileMessage: string | null
}

export interface FilterSet {
  key: string
  filters: Record<string, FilterValue[]>
}

export type FilterValue = { Name: string } | string | number

export interface GetBulkDownloadInfoResponse {
  result: boolean
  information: BulkDownloadInformation
}

export interface RefreshPercentageResponse {
  result: boolean
  information: BulkDownloadRefreshInfo
}

// GET /V2/Export/CheckBulkDownloadFileExists. `success: false` = the check itself failed.
export interface BulkDownloadFileExistsResponse {
  success: boolean
  fileExists?: boolean
}

// GET /V2/Export/GetBoReportDownloadLocation. A BackOffice-index report over the server's size
// limit cannot be served by DownloadExcelFromBulkDownload; the page gives its storage path.
export interface BoReportDownloadLocation {
  result: boolean
  tooLargeToDownload?: boolean
  storagePath?: string | null
  sizeInBytes?: number | null
}

// POST /V2/Export/RerunBulkDownload.
export interface RerunBulkDownloadResponse {
  success: boolean
}

/** What happened when the user asked to download the file. */
export type BulkDownloadFileOutcome =
  | { kind: 'started' }
  | { kind: 'file-not-found' }
  | { kind: 'too-large'; storagePath: string; sizeInBytes: number }
  | { kind: 'check-failed' }
  | { kind: 'failed' }

export interface GetFiltersUsedResponse {
  result: boolean
  information: {
    FiltersUsed: FilterSet[]
  }
}

export interface BulkDownloadInfoRow {
  info: string
  detail: string | number
}

// Per-lead export outcomes (GetBulkDownloadLeadOutcomes). Response casing is mixed on
// purpose — the summary fields are camelCase and the row fields PascalCase, exactly as
// ExportController returns them.
export interface LeadOutcomeItem {
  SourceId: number
  SimpleUrl: string
  // Encrypted lead id for /V2/companydetail/{id}; null when the lead never resolved to a
  // company, and the Info link then hides itself rather than opening an empty page.
  CompanyDetailId: string | null
  Status: string
  Reason: string | null
  ContactsRequested: number | null
  ContactsSelected: number | null
}

export interface LeadOutcomesResponse {
  result: boolean
  // false means the export predates outcome tracking — NOT that it was clean. The section
  // hides itself rather than rendering an empty table that would read as "nothing went wrong".
  recorded: boolean
  total?: number
  delivered?: number
  notDelivered?: number
  excluded?: number
  contactsRequested?: number
  contactsSelected?: number
  contactsShortfall?: number
  shortCount?: number
  matchingTotal?: number
  page?: number
  pageSize?: number
  items?: LeadOutcomeItem[]
}

export const LEAD_OUTCOMES_PAGE_SIZE = 10

// ---------------------------------------------------------------------------
// CRM drill-down (GetBulkDownloadExportedData / Exclusions / Errors)
// ---------------------------------------------------------------------------

export interface ExportedContact {
  Key: string
  Name: string
  Title: string | null
  Email: string | null
  EmailStatus: string | null
  Phone: string | null
  MobilePhone: string | null
  LinkedInUrl: string | null
  CrmContactId: string | null
  CrmExportedLink: string | null
  ErrorMessage: string | null
  // 'failed' when the CRM reported an error for this contact's key, 'success' otherwise.
  Status: string
}

export interface ExportedCompany {
  LeadId: string
  CompanyDetailId: string | null
  CompanyName: string
  Website: string | null
  Country: string | null
  CrmLeadId: string | null
  CrmCompanyId: string | null
  CrmObjectName: string | null
  CrmExportedLink: string | null
  ContactCount: number
  SelectedContactCount: number
  Contacts: ExportedContact[]
}

export interface ExportedDataSummary {
  TotalPushedCompanies: number
  TotalContactsOnPage: number
  TotalFailedContactsOnPage: number
  TotalExclusions: number
  TotalErrors: number
}

export interface ExportedDataResponse {
  result: boolean
  totalCount: number
  page: number
  pageSize: number
  summary: ExportedDataSummary
  items: ExportedCompany[]
}

export interface ExclusionItem {
  SimpleUrl: string
  Reason: number
  ReasonLabel: string
  RemovedFromList: boolean
}

export interface CrmErrorItem {
  ContactKey: string
  ContactName: string
  Error: string
}

export interface ExclusionsResponse {
  result: boolean
  totalCount: number
  items: ExclusionItem[]
}

export interface CrmErrorsResponse {
  result: boolean
  totalCount: number
  items: CrmErrorItem[]
}

export const EXPORTED_DATA_PAGE_SIZE = 20

// ---------------------------------------------------------------------------
// Removed-from-export items (GetCompaniesFromListBySimpleUrl / ...Contacts...)
// ---------------------------------------------------------------------------

/**
 * Only the fields this screen renders. The endpoints return the full company/contact list
 * models, but the modal shows a summary row, so the wide types are not worth carrying here.
 */
export interface RemovedCompany {
  CompanyId?: string
  Name: string
  Website: string
  Address?: string | null
  Industry?: string | null
  Turnover?: number | null
  LinkedInRange?: string | null
  Linkedin?: string | null
  Picture?: string | null
  ExclusionReasonId?: number | null
}

export interface RemovedContact {
  Key?: string
  Name: string
  Title?: string | null
  EmailAddress?: string | null
  SimpleUrl: string
  LinkedInUrl?: string | null
  Picture?: string | null
  Company?: { Name?: string; ExclusionReasonId?: number | null } | null
}

export interface GetCompaniesFromListResponse {
  result: boolean
  companies: RemovedCompany[]
}

export interface GetContactsFromListResponse {
  result: boolean
  contacts: RemovedContact[]
}

export const FILTER_NAMES: Record<string, string> = {
  CompanyName: 'Company Name / Website',
  ContactName: 'Contact Name / Email',
  ContactLocation: 'Contact Location',
  ContactTitle: 'Contact Title',
  ContactDepartment: 'Contact Department',
  ContactLevel: 'Contact Level',
  ContactIndustry: 'Contact Industry',
  GlobalRegion: 'Global Region',
  PaymentMethods: 'Payment Methods',
  FinanceProviders: 'Finance Providers',
  ShippingCompetitorInsights: 'Shipping Competitor Insights',
  ShippingRegion: 'Shipping Region',
  ShippingCountry: 'Shipping Country',
  ShippingEvidences: 'Shipping Evidences',
  ImportersExporters: 'Importers / Exporters',
  TopLevelDomains: 'Top Level Domains',
  VisitorTrafficRegion: 'Visitor Traffic Region',
  VisitorTrafficCountry: 'Visitor Traffic Country',
  CompaniesWithLocalizedSites: 'Companies with Localized Sites',
  EvidenceOfB2B: 'Evidence of B2B',
  EvidenceOfB2C: 'Evidence of B2C',
  HasPhysicalLocation: 'Has Physical Location',
  HasInternationalOffices: 'Has International Offices',
  IsMarketSeller: 'Is Market Seller',
  MustHaveEmail: 'Must have employee email',
  MustHaveCompanyEmail: 'Must have company email',
  MustHavePhoneNumber: 'Must have phone',
  MustHaveDirectDial: 'Must have direct dial/mobile phone',
  ShowLowConfidence: 'Show Low Confidence',
  NumberOfEmployees: 'No of employees',
  AgeOfSite: 'Age of site',
  SiteRank: 'Site Rank',
  GlobalTraffic: 'Global Traffic',
  EstimatedSales: 'Estimated sales',
  LegacyEstimatedSales: 'Legacy Estimated sales',
  HasAddToCart: 'Has add to cart',
  OnlyOnlinePresence: 'Only online presence',
  DontTakeOnlinePayments: "Don't take online payments",
  ListWebsites: 'Url',
  PaymentProviders: 'Payment Providers',
  HostingProviders: 'Hosting Providers',
  EmailHostingProviders: 'Email Hosting Providers',
  PaymentEvidences: 'Payment Evidences',
  ZipCode: 'Zip Code',
  BusinessCountry: 'Business Country',
  ContactLastEmailRefreshDate: 'Last Email Refresh Date',
  ContactLastUpdateDate: 'Last Contact Update Date',
  CompanyNameOrUrl: 'Company Name Or Url',
  ContactNameOrEmail: 'Contact Name Or Email',
  ContactEmailStatus: 'Contact Email Status',
  IsMonitoredHost: 'Monitored Host',
  HasCompanyNumber: 'Has Company Number',
  HasCompanyName: 'Has Company Name',
  HasVatNumber: 'Has Vat Number',
  IncludeHiddenLeads: 'Show Hidden Leads',
  OnlyHiddenLeads: 'Show Only Hidden Leads',
  UseBOIndex: 'Use BO Index',
  DemoMode: 'Demo Mode',
  TrialMode: 'Trial Mode',
  IncludeLocalizedDomains: 'Show Localized Domains',
  HasBrandReseller: 'Has Brand Reseller',
  HasOwnBrands: 'Has Own Brands',
  IncludeOnlyParent: 'Include Only Parent',
  IncludePurchasedLeads: 'Show Purchased Leads',
  IncludePurchasedContacts: 'Show Purchased Contacts',
  IncludeIgnoredLeads: 'Show Ignored Leads',
  IncludeIgnoredContacts: 'Show Ignored Contacts',
  HideCrmContacts: 'Hide CRM Contacts',
  FilterCrmLastInvoiceDate: 'Companies invoiced last 4 weeks',
  HideCrmLeads: 'Hide CRM Leads',
  HideLeadList: 'Hide LeadList Leads',
  HideLeadListContact: 'Hide LeadList Contacts',
  CompanySortBy: 'Sort Companies By',
  ContactSortBy: 'Sort Contacts By',
  AnyOnlinePayments: 'Has Online Payments',
  AnyShippingIndicator: 'All Sites Shipping',
  InternationalShipping: 'International Shipping',
  ImporterExporterInfoVolume: 'Import/Export Volume',
  ImporterExporterInfoFrequency: 'Import/Export Frequency',
  HasMultiPSP: 'Has Multi PSP',
  IsAuthorizedProvider: 'Authorized Provider',
  ContactMustHaveLinkedIn: 'Contact Must Have LinkedIn',
  ContactsAlertDateDetectedStartDate: 'Detected Date From',
  ContactsAlertDateDetectedEndDate: 'Detected Date To',
  ForeignExchange: 'Foreign Exchange',
}

export const CONTACT_REFRESH_FILTER = [10000, 365, 90, 60, 30, 7, 0] as const
export const CONTACT_REFRESH_LABELS = ['Never', '1yr', '90d', '60d', '30d', '7d', 'Today'] as const
